import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check, Copy, Minus, Plug, RefreshCw, Unplug } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/Spinner';
import {
  API_CONNECTION_PLATFORMS,
  type ApiConnectionDetails,
  type ApiConnectionPlatform,
} from '../../types/apiConnections';
import { getApiConnections, resetAccountConnection } from './api';
// File-level import (not the onboarding barrel) to avoid a barrel cycle —
// onboarding pages already import accounts/api the same way.
import { getGbpConnectUrl, getGoogleAdsConnectUrl, getInstagramConnectUrl } from '../onboarding/api';
import { GoogleAdsAccountPicker } from '../onboarding/GoogleAdsAccountPicker';
import { canOfferInstagramConnect } from '../../lib/instagramConnect';
import { googleAdsConnectAffordance } from '../../lib/googleAdsConnect';
import { formatStaleFor, isTokenStale, tokenStaleHours, TOKEN_STALE_HOURS } from '../../lib/tokenHealth';
import { ConnectionExpiredChip } from '../connectionHealth';
import { toast } from 'sonner';
import { facebookPageUrl, instagramUrl, metaAdAccountUrl } from '../../lib/connectionLinks';

// The connected-row identifier(s) to show next to the tick, per platform. id =
// a copyable identifier; name = optional muted label. Deep-links are CONSTRUCTED
// from the live ids/handle (never the stored *Url fields, which drift): idHref(id)
// links a copyable id chip (Meta ad account / Facebook Page); nameHref links the
// name label (Instagram handle — IG needs the handle, not the numeric id). Each
// href is null when not linkable, so we never render a dead link. Returns null
// when no detail is in hand.
type ConnectionDetail = {
  ids: string[];
  name: string | null;
  nameHref: string | null;
  idHref: (id: string) => string | null;
  // Prominent tag rendered as a Badge (Google Ads currency — the correctness
  // tell: NZD on an AU client means the wrong customer is bound).
  badge?: string | null;
};

const NO_HREF = (): string | null => null;

function platformDetail(
  platform: ApiConnectionPlatform,
  details: ApiConnectionDetails | null,
): ConnectionDetail | null {
  if (!details) return null;
  switch (platform) {
    case 'metaads':
      return details.metaadsScopedAdAccountIds.length
        ? { ids: details.metaadsScopedAdAccountIds, name: null, nameHref: null, idHref: metaAdAccountUrl }
        : null;
    case 'facebook':
      return details.facebookPageId
        ? { ids: [details.facebookPageId], name: details.facebookPageName, nameHref: null, idHref: facebookPageUrl }
        : null;
    case 'instagram':
      // The numeric IG id isn't linkable; the link comes from the handle (name).
      return details.instagramId
        ? {
            ids: [details.instagramId],
            name: details.instagramUsername ? `@${details.instagramUsername}` : null,
            nameHref: instagramUrl(details.instagramUsername),
            idHref: NO_HREF,
          }
        : null;
    case 'googleads':
      // Account name (when the global cache resolves it) + copyable customer id +
      // a prominent currency badge (the correctness tell). Currency shows "—" when
      // the cache can't resolve it — never the stale metadata currency.
      return details.googleAdsCustomerId
        ? {
            ids: [details.googleAdsCustomerId],
            name: details.googleAdsAccountName,
            nameHref: null,
            idHref: NO_HREF,
            badge: details.googleAdsCurrency ?? '—',
          }
        : null;
    case 'googlebusiness':
      // The location DISPLAY name (which location is attached) + its id chip.
      return details.googleBusinessLocationName
        ? {
            ids: details.googleBusinessLocationId ? [details.googleBusinessLocationId] : [],
            name: details.googleBusinessLocationName,
            nameHref: null,
            idHref: NO_HREF,
          }
        : null;
    default:
      return null;
  }
}

// Amber "token may need reconnecting" chip for one platform row. The signal is
// `tokenExpiresAt` sitting WELL in the past (see src/lib/tokenHealth.ts) — the only
// tell we get when a grant is revoked, since Zernio's own needsReconnection /
// adsStatus stay clean. So the wording HEDGES: it states the observed fact (expired
// N ago) and says "may need reconnecting", never "dead".
function TokenStaleChip({
  expiresAt,
  nowMs,
  action,
}: {
  expiresAt: string;
  nowMs: number;
  // The platform's reconnect affordance, rendered alongside (a Link for the
  // Meta-family onboarding re-auth, a button for the in-place Google connects).
  action?: React.ReactNode;
}) {
  const staleFor = formatStaleFor(tokenStaleHours(expiresAt, nowMs));
  return (
    <span className="flex flex-wrap items-center gap-2">
      <Badge
        variant="warning"
        className="gap-1"
        title={
          `The stored OAuth token expired ${staleFor} ago and has not been refreshed ` +
          `(a healthy connection refreshes about hourly, so anything past ` +
          `${TOKEN_STALE_HOURS}h means the refresh has stopped — usually a revoked grant). ` +
          `Expired at ${expiresAt}. This is a heuristic: the platform may still report ` +
          `itself connected. If reconnecting doesn't re-prompt for consent, reset the ` +
          `connection and set it up fresh.`
        }
      >
        <AlertTriangle className="h-3 w-3" />
        Token expired {staleFor} ago — may need reconnecting
      </Badge>
      {action}
    </span>
  );
}

// Account page "API Connections" sub-section (under "Ad accounts & URLs").
// Read-only view of the account's Zernio profile + which platforms are
// actually connected (live Zernio state, 5-min server cache, manual refresh).
// Meta gets a "Connect" action that deep-links into the onboarding wizard
// with this account preselected; the other five are display-only this pass.

const PLATFORM_LABEL: Record<ApiConnectionPlatform, string> = {
  metaads: 'Meta Ad Account',
  facebook: 'Facebook Page',
  instagram: 'Instagram Page',
  googlebusiness: 'Google My Business',
  googleads: 'Google Ads',
  tiktok: 'TikTok Page',
};

export function ApiConnectionsSection({ accountId }: { accountId: string }) {
  const queryClient = useQueryClient();
  const connections = useQuery({
    queryKey: ['apiConnections', accountId],
    queryFn: () => getApiConnections(accountId),
  });
  const refresh = useMutation({
    mutationFn: () => getApiConnections(accountId, true),
    onSuccess: (data) => queryClient.setQueryData(['apiConnections', accountId], data),
  });
  const [confirmReset, setConfirmReset] = useState(false);
  const reset = useMutation({
    mutationFn: () => resetAccountConnection(accountId),
    onSuccess: () => {
      setConfirmReset(false);
      // The account is now un-onboarded — refresh the connections view + the
      // detail page's Ad-accounts fields + any account lists.
      void queryClient.invalidateQueries({ queryKey: ['apiConnections', accountId] });
      void queryClient.invalidateQueries({ queryKey: ['account', accountId] });
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });

  // GBP connect: fetch the Google consent URL, then hand the browser off.
  const gbpConnect = useMutation({
    mutationFn: () => getGbpConnectUrl(accountId),
    onSuccess: ({ authUrl }) => {
      window.location.href = authUrl;
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Instagram connect (per-account, standalone OAuth): fetch the IG consent URL,
  // then hand the browser off. The standard-mode redirect lands on
  // /onboarding/instagram/callback (no picker — IG needs no sub-selection).
  const instagramConnect = useMutation({
    mutationFn: () => getInstagramConnectUrl(accountId),
    onSuccess: ({ authUrl }) => {
      window.location.href = authUrl;
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Google Ads connect: OAuth (redirect to authUrl). If Zernio reports the
  // googleads account is already connected (no authUrl), skip OAuth and open the
  // picker inline — the "Change"/re-pick path needs no re-OAuth.
  const [googleAdsPickerOpen, setGoogleAdsPickerOpen] = useState(false);
  const googleAdsConnect = useMutation({
    mutationFn: () => getGoogleAdsConnectUrl(accountId),
    onSuccess: ({ authUrl }) => {
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        setGoogleAdsPickerOpen(true);
      }
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const data = connections.data;
  // Token staleness is measured against NOW, not the payload's fetchedAt — the
  // 5-min server cache doesn't move the answer, and "how long expired" should read
  // as of looking at it.
  const nowMs = Date.now();
  const hasProfile = Boolean(data?.profileId);
  const statusUnknown = hasProfile && (!data?.available || data?.platforms === null);
  // Ghost profile: the linked Zernio profile was deleted in Zernio (definitive
  // 404). Reconnect/re-onboard are wedged until the connection is reset.
  const profileGone = hasProfile && data?.profileExists === false;
  // Honesty (PART E): status is last-known as of this resolution, not real-time.
  const checkedAt = data?.fetchedAt
    ? new Date(data.fetchedAt).toLocaleString('en-AU', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <div className="mt-6 border-t border-slate-100 pt-4">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-slate-900">API Connections</h3>
        {hasProfile && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            title="Refresh connection status"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
          >
            <RefreshCw
              className={refresh.isPending ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'}
            />
          </Button>
        )}
        {checkedAt && !statusUnknown && (
          <span className="text-xs text-slate-400" title="Last-known status — not real-time">
            checked {checkedAt}
          </span>
        )}
      </div>

      {/* Live token-death banner (distinct from the "exists but not connected"
          state below): a platform's fetches are actively FAILING → reconnect. */}
      <ConnectionExpiredChip accountId={accountId} className="mt-3" />

      {/* Ghost profile: the linked Zernio profile no longer exists. Reconnect
          reuses the dead id and re-onboard is blocked — resetting the connection
          is the only way forward. */}
      {profileGone && (
        <div className="mt-3 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold">This Zernio profile no longer exists</p>
            <p className="mt-0.5">
              The linked profile was deleted in Zernio, so reconnect and re-onboard are both
              blocked. Reset the connection to clear the stale link, then onboard the account
              fresh.
            </p>
            <Button
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={() => setConfirmReset(true)}
              disabled={reset.isPending}
            >
              <Unplug className="h-4 w-4" />
              Reset connection
            </Button>
          </div>
        </div>
      )}

      {connections.isLoading ? (
        <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
          <Spinner className="h-4 w-4" /> Checking connections…
        </div>
      ) : connections.isError ? (
        <p className="mt-3 text-sm text-slate-500">Connection status unavailable.</p>
      ) : !hasProfile ? (
        <div className="mt-3 flex items-center gap-3">
          <p className="text-sm text-slate-500">No profile connected.</p>
          <Button asChild size="sm">
            <Link to={`/onboarding?account=${accountId}`}>
              <Plug className="h-4 w-4" />
              Set up connection
            </Link>
          </Button>
        </div>
      ) : (
        <div className="mt-3">
          <div className="flex items-center gap-2 text-sm">
            <span className="w-40 shrink-0 text-slate-500">Profile ID</span>
            <CopyableId value={data!.profileId!} />
            {data!.profileName && (
              <span className="truncate text-xs text-slate-400">({data!.profileName})</span>
            )}
          </div>

          <ul className="mt-2 space-y-1.5">
            {API_CONNECTION_PLATFORMS.map((platform) => {
              const connected = data!.platforms?.[platform] ?? null;
              const detail = connected ? platformDetail(platform, data!.details) : null;
              const drift = platform === 'metaads' ? data!.adAccountDrift : null;
              // Stale token — only meaningful on a CONNECTED row: an unconnected
              // platform already reads "Not connected", and its leftover expiry
              // says nothing new.
              const tokenExpiresAt = data!.tokenExpiry?.[platform] ?? null;
              const tokenStale =
                connected === true && isTokenStale(tokenExpiresAt, nowMs)
                  ? (tokenExpiresAt as string)
                  : null;
              return (
                <li key={platform} className="flex items-start gap-2 text-sm">
                  <span className="w-40 shrink-0 pt-0.5 text-slate-500">
                    {PLATFORM_LABEL[platform]}
                  </span>
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
                    {statusUnknown ? (
                      <Badge variant="neutral">status unavailable</Badge>
                    ) : connected ? (
                      <span className="flex items-center gap-1 text-emerald-600">
                        <Check className="h-4 w-4" />
                        Connected
                      </span>
                    ) : (
                      <span
                        className="flex items-center gap-1 text-slate-400"
                        title={
                          platform === 'metaads' ||
                          platform === 'googlebusiness' ||
                          platform === 'instagram' ||
                          platform === 'googleads'
                            ? undefined
                            : 'Connection via kyck coming soon'
                        }
                      >
                        <Minus className="h-4 w-4" />
                        Not connected
                      </span>
                    )}

                    {/* Connected-row identifier(s): name (muted, linked for IG) +
                        copyable id chip (linked for Meta ad account / FB Page). */}
                    {detail && (
                      <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                        {detail.name &&
                          (detail.nameHref ? (
                            <a
                              href={detail.nameHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="truncate text-xs font-medium text-kyck-600 hover:underline"
                            >
                              {detail.name}
                            </a>
                          ) : (
                            <span className="truncate text-xs text-slate-500">{detail.name}</span>
                          ))}
                        {detail.ids.map((id) => (
                          <CopyableId key={id} value={id} title="Copy id" href={detail.idHref(id)} />
                        ))}
                        {detail.badge && (
                          <Badge variant="neutral" className="font-mono">
                            {detail.badge}
                          </Badge>
                        )}
                      </span>
                    )}

                    {/* Stale OAuth token: the connection reads connected but its
                        token stopped refreshing (probably revoked). Reconnect
                        affordance by platform — Meta-family goes through the
                        onboarding full re-auth (a re-scope wouldn't renew a dead
                        grant); the Google connects can re-run in place. */}
                    {tokenStale && (
                      <TokenStaleChip
                        expiresAt={tokenStale}
                        nowMs={nowMs}
                        action={
                          platform === 'metaads' ||
                          platform === 'facebook' ||
                          platform === 'instagram' ? (
                            <Button asChild size="sm" variant="outline" className="h-7">
                              <Link to={`/onboarding?reconnect=${accountId}&mode=page`}>
                                Reconnect
                              </Link>
                            </Button>
                          ) : platform === 'googleads' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7"
                              onClick={() => googleAdsConnect.mutate()}
                              disabled={googleAdsConnect.isPending}
                            >
                              {googleAdsConnect.isPending ? 'Starting…' : 'Reconnect'}
                            </Button>
                          ) : platform === 'googlebusiness' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7"
                              onClick={() => gbpConnect.mutate()}
                              disabled={gbpConnect.isPending}
                            >
                              {gbpConnect.isPending ? 'Starting…' : 'Reconnect'}
                            </Button>
                          ) : null
                        }
                      />
                    )}

                    {/* Drift: ad account changed away from the profile's scope. */}
                    {drift && (
                      <span className="flex flex-wrap items-center gap-2">
                        <Badge variant="warning" className="gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          Ad account changed — reconnect to re-scope
                        </Badge>
                        <Button asChild size="sm" variant="outline" className="h-7">
                          <Link to={`/onboarding?reconnect=${accountId}&mode=ad-account`}>
                            Reconnect
                          </Link>
                        </Button>
                      </span>
                    )}

                    {/* Community Inbox Phase 0b: GBP connect — server obtains
                        the Zernio googlebusiness consent URL; the callback
                        lands on /onboarding/gbp/callback (location picker). */}
                    {platform === 'googlebusiness' && !statusUnknown && connected === false && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7"
                        onClick={() => gbpConnect.mutate()}
                        disabled={gbpConnect.isPending}
                      >
                        {gbpConnect.isPending ? 'Starting…' : 'Connect'}
                      </Button>
                    )}

                    {/* Instagram connect — per-account standalone OAuth (no
                        picker). Server obtains the Zernio IG consent URL; the
                        standard-mode redirect lands on
                        /onboarding/instagram/callback. */}
                    {platform === 'instagram' &&
                      canOfferInstagramConnect({ hasProfile, statusUnknown, connected }) && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7"
                        onClick={() => instagramConnect.mutate()}
                        disabled={instagramConnect.isPending}
                      >
                        {instagramConnect.isPending ? 'Starting…' : 'Connect'}
                      </Button>
                    )}

                    {/* Google Ads connect / re-pick. connect → OAuth then the
                        picker (callback page); change → open the picker inline
                        (no re-OAuth) to correct a wrong binding. */}
                    {platform === 'googleads' &&
                      (() => {
                        const affordance = googleAdsConnectAffordance({
                          hasProfile,
                          statusUnknown,
                          connected,
                        });
                        if (affordance === 'connect') {
                          return (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7"
                              onClick={() => googleAdsConnect.mutate()}
                              disabled={googleAdsConnect.isPending}
                            >
                              {googleAdsConnect.isPending ? 'Starting…' : 'Connect'}
                            </Button>
                          );
                        }
                        if (affordance === 'change') {
                          return (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7"
                              onClick={() => setGoogleAdsPickerOpen(true)}
                            >
                              Change
                            </Button>
                          );
                        }
                        return null;
                      })()}

                    {platform === 'metaads' && !statusUnknown && connected === false && (
                      <Button asChild size="sm" variant="outline" className="h-7">
                        {/* Mode by connection state: if the Page is still
                            connected, a wrong/missing ad account is an
                            ad-account re-scope; if the Page is gone too, it's a
                            full page reconnect (re-OAuth). */}
                        <Link
                          to={`/onboarding?reconnect=${accountId}&mode=${
                            data!.platforms?.facebook === true ? 'ad-account' : 'page'
                          }`}
                        >
                          Connect
                        </Link>
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          {/* General manual unlink. Hidden when the ghost banner is up (it has
              its own Reset button). Behind the same confirm — destructive but
              recoverable (re-onboarding re-links the account). */}
          {!profileGone && (
            <div className="mt-3 border-t border-slate-100 pt-2">
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-rose-600"
                onClick={() => setConfirmReset(true)}
                disabled={reset.isPending}
              >
                <Unplug className="h-3.5 w-3.5" />
                Reset connection
              </button>
            </div>
          )}
        </div>
      )}

      {googleAdsPickerOpen && (
        <GoogleAdsPickerModal
          accountId={accountId}
          onClose={() => setGoogleAdsPickerOpen(false)}
          onBound={() => {
            setGoogleAdsPickerOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['apiConnections', accountId] });
            void queryClient.invalidateQueries({ queryKey: ['account', accountId] });
          }}
        />
      )}

      {confirmReset && (
        <ResetConnectionModal
          profileName={data?.profileName ?? null}
          profileGone={profileGone}
          pending={reset.isPending}
          error={reset.isError ? (reset.error as Error).message : null}
          onCancel={() => {
            if (!reset.isPending) {
              reset.reset();
              setConfirmReset(false);
            }
          }}
          onConfirm={() => reset.mutate()}
        />
      )}
    </div>
  );
}

// Confirm modal for the connection reset — mirrors DeleteAccountModal's overlay/
// layout, but a plain confirm (no type-to-confirm word) since a reset is
// recoverable: re-onboarding re-links the account.
function ResetConnectionModal({
  profileName,
  profileGone,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  profileName: string | null;
  profileGone: boolean;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !pending) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pending, onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reset-connection-title"
    >
      <div className="w-full max-w-lg rounded-md bg-white shadow-xl">
        <div className="space-y-3 p-6 text-sm text-slate-700">
          <h2 id="reset-connection-title" className="text-lg font-semibold text-slate-900">
            Reset connection?
          </h2>
          <p>
            This unlinks{' '}
            {profileName ? (
              <span className="font-medium">{profileName}</span>
            ) : (
              'this account'
            )}{' '}
            from its Zernio profile and clears the stored Meta ad account, Instagram, pixel
            and lead-form defaults. The account returns to <span className="font-mono">un-onboarded</span>.
          </p>
          <ul className="list-disc pl-5 text-slate-600">
            <li>
              {profileGone
                ? 'The Zernio profile is already gone — nothing is deleted in Zernio.'
                : 'Best-effort deletes the Zernio profile if it has no connected accounts; a connected profile is left intact.'}
            </li>
            <li>
              Any unfinished onboarding or reconnect for this account is discarded, so the next
              onboard starts fresh rather than resuming the old profile.
            </li>
            <li>You can re-onboard the account fresh (a new profile) right after.</li>
            <li>An audit log entry records the reset.</li>
          </ul>
          {error && (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-rose-800">
              {error}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4">
          <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
            Keep connection
          </Button>
          <Button type="button" variant="destructive" onClick={onConfirm} disabled={pending}>
            {pending ? 'Resetting…' : 'Reset connection'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Inline picker dialog for re-picking (or first-picking, when already connected)
// the Google Ads account without leaving the page. Hosts GoogleAdsAccountPicker;
// same overlay style as ResetConnectionModal.
function GoogleAdsPickerModal({
  accountId,
  onClose,
  onBound,
}: {
  accountId: string;
  onClose: () => void;
  onBound: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="google-ads-picker-title"
    >
      <div className="w-full max-w-lg rounded-md bg-white shadow-xl">
        <div className="space-y-3 p-6">
          <div>
            <h2 id="google-ads-picker-title" className="text-lg font-semibold text-slate-900">
              Google Ads account
            </h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Pick the Google Ads customer for this client. Check the currency matches the client's
              country.
            </p>
          </div>
          <GoogleAdsAccountPicker accountId={accountId} onDone={onBound} />
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

function CopyableId({
  value,
  title = 'Copy',
  href,
}: {
  value: string;
  title?: string;
  href?: string | null;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <span className="flex items-center gap-1">
      {href ? (
        // Deep-link to the live asset on Meta (constructed from the id). The id
        // stays copyable via the button alongside — the link is additive.
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-kyck-600 hover:underline"
        >
          {value}
        </a>
      ) : (
        <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-700">
          {value}
        </code>
      )}
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6"
        title={title}
        onClick={() => {
          void navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
      >
        {copied ? (
          <Check className="h-3 w-3 text-emerald-600" />
        ) : (
          <Copy className="h-3 w-3" />
        )}
      </Button>
    </span>
  );
}
